import { ReactElement, ReactNode } from 'react'

import { Row } from 'antd'
import isUndefined from 'lodash/isUndefined'
import toNumber from 'lodash/toNumber'

import { ChangeDelta } from 'components/ChangeDelta'
import { Flex } from 'components/core'
import { CHANGE, PERCENTAGE_CHANGE } from 'const/metrics'
import {
    formatAbbreviatedMetric,
    formatCerebroDate,
    formatNumber,
    withCurrencyLocale,
} from 'helpers/formatting'
import { shortNameToNode } from 'helpers/typeGuard'
import { transformToString } from 'helpers/widgets'
import { CurrencyCode, Widget, FieldMapping } from 'types'

import { isPercentMetric } from '../TimeSeriesWidget/helpers'

import * as styles from './styles.scss'

interface Props {
    widget: Widget
    widgetData: any[]
    metricsConfig: FieldMapping
    currencyCode?: CurrencyCode
}

function MetricWidget({
    widget,
    widgetData,
    metricsConfig,
    currencyCode,
}: Props): ReactElement {
    const metricsObj = widgetData?.[0] ?? {}
    const widgetMetrics = transformToString(widget.metrics) as string[]

    function renderPeriodDelta(metric: string): ReactNode {
        if (widget.constant_value_for_prior_period) {
            return widget.constant_value_for_prior_period
        }

        const changeValue = metricsObj[`${metric}__${CHANGE}`]
        const percentageChangeValue =
            metricsObj[`${metric}__${PERCENTAGE_CHANGE}`]
        if (isUndefined(metricsConfig[metric])) {
            return null
        }
        const config = metricsConfig[metric]
        const metricOptions = config?.metricOptions

        if (isUndefined(changeValue)) {
            return null
        }

        const deltas = []

        if (percentageChangeValue && !isPercentMetric(metricOptions?.type)) {
            deltas.push(
                <ChangeDelta
                    key="percentage_change"
                    value={toNumber(percentageChangeValue)}
                    formatter={(value) => `${formatNumber(value, '0,0.00')}%`}
                    inverse={metricOptions?.isInverse}
                    style={{
                        padding: '0px 4px',
                    }}
                />
            )
        }

        deltas.push(
            <ChangeDelta
                key="change"
                value={toNumber(changeValue)}
                formatter={(value: number | string): string =>
                    withCurrencyLocale(currencyCode, () =>
                        widget.show_full_value
                            ? formatNumber(
                                  value,
                                  metricOptions?.format ?? '0,0',
                                  false,
                                  metricOptions?.type === 'percentage_as_is'
                              )
                            : formatAbbreviatedMetric(
                                  value,
                                  metricOptions?.format ?? '0,0',
                                  metricOptions?.shortFormat ?? '',
                                  false,
                                  metricOptions?.type === 'percentage_as_is'
                              )
                    )
                }
                inverse={metricOptions?.isInverse}
                style={{
                    padding: '0px 4px',
                }}
            />
        )

        return deltas
    }

    function formatFullValueMetric(
        metric: string,
        metrics: Record<string, any>
    ): string {
        if (metricsConfig[metric]?.metricOptions?.format === 'YYYY-MM-DD') {
            return formatCerebroDate(metrics[metric])
        }
        return formatNumber(
            metrics[metric],
            metricsConfig[metric]?.metricOptions?.format,
            false,
            metricsConfig[metric]?.metricOptions?.type === 'percentage_as_is'
        )
    }

    return (
        <Row className="align-items-start align-content-start metrics-container">
            {widgetMetrics.flatMap((metric): ReactElement | ReactElement[] => {
                if (isUndefined(metricsConfig[metric])) {
                    return []
                }
                return (
                    <Flex
                        direction="column"
                        key={metric}
                        className={styles.metric}
                        alignItems="normal"
                    >
                        <p className={styles['metric-title']}>
                            {shortNameToNode(
                                metricsConfig[metric]?.shortName
                            ) ?? '-'}
                        </p>
                        <p className={styles['metric-value']}>
                            {withCurrencyLocale(currencyCode, () =>
                                widget.show_full_value
                                    ? formatFullValueMetric(metric, metricsObj)
                                    : formatAbbreviatedMetric(
                                          metricsObj[metric],
                                          metricsConfig[metric]?.metricOptions
                                              ?.format ?? '0,0',
                                          metricsConfig[metric]?.metricOptions
                                              ?.shortFormat ?? '',
                                          false,
                                          metricsConfig[metric]?.metricOptions
                                              ?.type === 'percentage_as_is'
                                      )
                            )}
                        </p>
                        <Flex
                            gap={6}
                            className={styles['metric-delta']}
                            wrap="wrap"
                        >
                            {renderPeriodDelta(metric)}
                        </Flex>
                    </Flex>
                )
            })}
        </Row>
    )
}

export default MetricWidget
